import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {Network, NetworkForm, NetworkService} from "../../../../../../../services/back/network.service";
import {NetworkRole, SubnetMask} from "../../../../../../../services/model/server.model";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {
	ipAddressValidator, isNetworkConfigurationValid,
	subnetValidator
} from "../../../../../../../utils/forms.utils";
import {distinctUntilChanged, finalize, Observable, of, Subscription, switchMap, takeWhile, tap} from "rxjs";
import {filter, map} from "rxjs/operators";
import {NetworkDetailService} from "../../../../../../../services/front/network-detail.service";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {SnackbarService} from "../../../../../../../services/front/snackbar.service";
import {DesignSystemModule} from "../../../../../../design-system/design-system.module";
import {NgForOf, NgIf} from "@angular/common";
import {CurrentTenantService} from "../../../../../../../services/front/current-tenant.service";
import {IpCheckerService} from "../../../../../../../services/front/ip-checker.service";

@Component({
	selector: 'app-network-information-form',
	standalone: true,
	imports: [
		DesignSystemModule,
		NgForOf,
		NgIf,
		TranslateModule
	],
	templateUrl: './network-information-form.component.html',
	styleUrl: './network-information-form.component.scss'
})
export class NetworkInformationFormComponent implements OnInit, OnDestroy {
	@Input() network: Network;

	tenantId: string;
	isEditor: boolean;
	_saving: boolean;

	advancementPercent: number = 0;

	networkRoleList: NetworkRole[] = Object.values(NetworkRole);
	subnetMaskList: SubnetMask[] = Object.values(SubnetMask).reverse();
	filteredSubnetMaskList: SubnetMask[] = this.subnetMaskList;

	subnetMaskSearchFormControl: FormControl = new FormControl<string | null>(null);

	formGroup: FormGroup;

	subscription: Subscription = new Subscription();

	constructor(private networkDetailService: NetworkDetailService,
				private networkService: NetworkService,
				private translateService: TranslateService,
				private snackBarService: SnackbarService,
				private ipCheckerService: IpCheckerService,
				private currentTenantService: CurrentTenantService) {
	}

	ngOnInit() {
		this.subscription.add(this.currentTenantService.getCurrentTenantChanges()
			.subscribe(tenant => {
				this.tenantId = tenant.configuration.id
				this.isEditor = tenant.configuration.role !== 'read_only';
			}));
		this.buildForm();
		const values: any[] = [this.network.role, this.network.vlanId, this.network.ipAddress, this.network.subnet];
		this.advancementPercent = (values.filter(v => !!v).length / values.length) * 100;
		this.subscription.add(this.subnetMaskSearchFormControl.valueChanges
			.subscribe(search => this.filteredSubnetMaskList = this.subnetMaskList.filter(subnet => subnet.includes(search) || this.cidrToMask(subnet).includes(search))));
	}

	buildForm(): void {
		this.formGroup = new FormGroup({
			role: new FormControl<NetworkRole>(this.network.role, [Validators.required]),
			ipAddress: new FormControl<string>(this.network.ipAddress, [Validators.required, ipAddressValidator()]),
			subnetMask: new FormControl<SubnetMask>(this.network.subnet, [Validators.required, subnetValidator()]),
			vlanId: new FormControl<string | null>(this.network.vlanId),
		});

		this.subscription.add(this.roleFormControl.valueChanges.pipe(distinctUntilChanged())
			.subscribe(() => this.updateNetworkInformation()));
		this.subscription.add(this.subnetMaskFormControl.valueChanges.pipe(
			distinctUntilChanged(),
			tap(() => this.ipAddressFormControl.setValue(this.network.ipAddress, {emitEvent: false}))
		).subscribe(() => this.updateNetworkInformation()));
	}

	updateNetworkInformation(): void {
		this.switchSaving()
			.pipe(
				filter(() => this.hasInformationChanged() && this.formGroup.valid),
				map(() => this.buildNetworkForm()),
				switchMap(form => this.networkService.update(this.tenantId, this.network.id, form)),
				tap((updateSucceeded) => this.displayUpdatedSucceed(updateSucceeded)),
				tap(() => this.networkDetailService.refreshNetwork()),
				takeWhile(() => !this.subscription.closed),
				finalize(() => this.switchSaving()))
			.subscribe();
	}

	hasInformationChanged(): boolean {
		return this.network.role !== (this.roleFormControl.value)
			|| this.network.ipAddress !== (this.ipAddressFormControl.value)
			|| this.network.subnet !== (this.subnetMaskFormControl.value)
			|| this.network.vlanId !== (this.vlanIdFormControl.value || null);
	}

	buildNetworkForm(): NetworkForm {
		return {
			name: this.network.name,
			role: this.roleFormControl.value,
			ipAddress: this.ipAddressFormControl.value,
			subnet: this.subnetMaskFormControl.value,
			description: this.network.description || undefined,
			vlanId: this.vlanIdFormControl.value || undefined,
		}
	}

	displayUpdatedSucceed(updateSucceed: boolean): void {
		if (updateSucceed) {
			this.snackBarService.show(this.translateService.instant('page.application.detail.update.success'));
		} else {
			this.snackBarService.show(this.translateService.instant('page.application.detail.update.fail'), 'danger-snack');
		}
	}

	resetIpAddressFormControl(): void {
		if (this.ipAddressFormControl.invalid)
			this.ipAddressFormControl.setValue(this.network.ipAddress, {emitEvent: false});
	}

	switchSaving(): Observable<{}> {
		this._saving = !this._saving;
		return of({});
	}

	cidrToMask(cidr: string): string {
		return this.ipCheckerService.cidrToSubnetMask(cidr);
	}

	ngOnDestroy() {
		this.subscription.unsubscribe();
	}

	get roleFormControl(): FormControl {
		return this.formGroup.get('role') as FormControl;
	}

	get ipAddressFormControl(): FormControl {
		return this.formGroup.get('ipAddress') as FormControl;
	}

	get subnetMaskFormControl(): FormControl {
		return this.formGroup.get('subnetMask') as FormControl;
	}

	get vlanIdFormControl(): FormControl {
		return this.formGroup.get('vlanId') as FormControl;
	}

	protected readonly isNetworkConfigurationValid = isNetworkConfigurationValid;
}
